import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { qrcodeAPIService } from '~/api/qrcode';
import { SelectQrType } from '~/components/organisms/SelectQrType/SelectQrType';
import { MICROSITE_TYPES, NEW_QR_CODES_INDEXES } from '~/constants/common';
import {
  QR_CODE_BUILDER_CREATED_ID_PARAM_NAME,
  QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME,
  QR_CODE_ID_PARAM_NAME,
  QR_CODE_TEMPLATE_PARAM_NAME,
  QR_CODE_TYPE,
} from '~/constants/qr-code';
import { templates } from '~/constants/queryParams';
import { PATH } from '~/constants/route-path';
import { useAppDispatch } from '~/hooks/useRedux';
import { qrCodeSlice, updateQrCodeThunk } from '~/store/reducers/qr-code';
import { createQrCodeStylesThunk, createQrCodeThunk } from '~/store/reducers/qr-code';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import useBuilderStore from '~/store/useBuilderStore';

import '../styles/index.scss';

// only for (1) Create dynamic (2) create static and (3) edit dynamic
export default function CreateQrTemplateStep() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isEditPage = searchParams.get(QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME) || null;
  const createdId = searchParams.get(QR_CODE_BUILDER_CREATED_ID_PARAM_NAME) || null;
  const anonymousMicrositeId = searchParams.get('anonymous_microsite_id') || null;

  const { setActiveStep } = useBuilderStore();

  const qrCode = useSelector(getQrCodeReducerData);

  const isQrcodeDataExist = !!qrCode.data.id;

  const onNextStep = () => setActiveStep(1);

  const { mutate: createQrCodeMutate, isPending: isPendingCreateQrCodeMutate } = useMutation({
    mutationFn: qrcodeAPIService.createQrCode,
    onSuccess: (response) => {
      searchParams.set(QR_CODE_TEMPLATE_PARAM_NAME, response.data.micrositeType);
      searchParams.set(QR_CODE_ID_PARAM_NAME, response.data.id);
      setSearchParams(searchParams);
      onNextStep();
    },
  });

  const { mutate: updateQrCodeMutate, isPending: isPendingUpdateQrCodeMutate } = useMutation({
    mutationFn: async (payload: any) => {
      const { id, ...body } = payload;
      return await qrcodeAPIService.updateQrCode(body, id);
    },
  });

  const handleDynamicQrTypeClick = async (templateId: number) => {
    try {
      const commonPayload = {
        type: QR_CODE_TYPE.DYNAMIC,
        micrositeType: MICROSITE_TYPES[templateId],
      };
      const selectedTemplate = templates[templateId];

      if (isEditPage && createdId) {
        const payload = { ...commonPayload, id: createdId };
        if (NEW_QR_CODES_INDEXES.includes(templateId)) {
          updateQrCodeMutate(payload);
        } else {
          dispatch(updateQrCodeThunk(payload));
        }

        searchParams.set('template', selectedTemplate);
        setSearchParams(searchParams);
        onNextStep();
      } else {
        const payload = { ...commonPayload, style: qrCode.qrStyle };
        if (NEW_QR_CODES_INDEXES.includes(templateId)) {
          createQrCodeMutate(payload);
        } else {
          const action = () => {
            searchParams.set('template', selectedTemplate);
            setSearchParams(searchParams);
            onNextStep();
          };
          dispatch(createQrCodeThunk({ payload, action }));
        }
      }

      dispatch(createQrCodeStylesThunk(true));
    } catch (error) {
      // actionsAlert.addAlert({
      //   text: t('labels:defaultTrialWarningMessage', { endTime: new Date().toLocaleDateString() }),
      //   type: 'warning',
      //   variant: 'filled',
      //   id: Date.now(),
      // });
    }
  };

  const handleCreateStaticQR = (staticType) => {
    navigate(PATH.QR.CREATE_QR_STATIC_CONTENT(staticType));
  };

  useEffect(() => {
    if (anonymousMicrositeId) {
      handleDynamicQrTypeClick(anonymousMicrositeId);
      dispatch(qrCodeSlice.actions.setSelectedQr(null));
    }

    if (typeof qrCode?.selectedQR === 'number' && !isEditPage) {
      // at the moment hardcoded qr type 0 , because we have only dynamic type
      handleDynamicQrTypeClick(qrCode?.selectedQR);
      dispatch(qrCodeSlice.actions.setSelectedQr(null));
    }
  }, [qrCode?.selectedQR]);

  return (
    <SelectQrType
      isShowAlert={isQrcodeDataExist}
      onDynamicQrTypeClick={handleDynamicQrTypeClick}
      onStaticQrTypeClick={handleCreateStaticQR}
      isStaticDisabled={qrCode.loadingSet.isCreateQrcodeLoading || isQrcodeDataExist}
      isDynamicDisabled={
        qrCode.loadingSet.isCreateQrcodeLoading || isPendingCreateQrCodeMutate || isPendingUpdateQrCodeMutate
      }
    />
  );
}
